import { supabase } from '@/integrations/supabase/client';
import { logTicketEventToOcis } from '@/utils/ocisIngest';
import { getTodayInShopTz } from '@/utils/shopTime';
import { fromZonedTime } from 'date-fns-tz';
// DTC-G G4: route every event-occurrence write timestamp + UTC-day
// fallback through the canonical clock + `getTodayInShopTz('UTC')`.
// Event-log rows are auditable business data; deterministic timestamps
// let test fixtures freeze the audit trail under `clock.setFixed(...)`.
import { clock } from '@/utils/clock';

/**
 * FIX-R36-LOW-2: shared helper that wraps `pensieve_events.insert` and
 * surfaces failures via console with shop/ticket/event_type context.
 *
 * Previously every insert in this file was awaited but the result was
 * never destructured, so analytics drops (RLS regression, schema drift,
 * connection blip) were completely silent. This helper centralizes the
 * error path so a future migration to a remote sink only touches one
 * function.
 *
 * Returns a promise that resolves to `null` on success and the error on
 * failure — callers may inspect or ignore.
 */
async function insertPensieveEvent(payload: any): Promise<unknown | null> {
  const { error } = await supabase.from('pensieve_events').insert(payload);
  if (error) {
    console.error('[ticketEventLogger] pensieve insert failed:', error, {
      shop_id: payload?.shop_id,
      ticket_id: payload?.ticket_id,
      event_type: payload?.event_type,
    });
    return error;
  }
  return null;
}

/**
 * Log ticket creation event (dual-write: Pensieve + OCIS)
 */
export async function logTicketCreated(params: {
  ticketId: string;
  shopId: string;
  userId: string;
  ticketType: 'Waiter' | 'Drop-off';
  totalMinutes: number;
  promisedTime?: string;
  scheduledStartTime?: string;
  shopTimezone?: string;
}) {
  // R8-L-3: Use shop TZ for daily metrics key
  const dateStr = params.shopTimezone ? getTodayInShopTz(params.shopTimezone) : getTodayInShopTz('UTC');

  // Log pensieve event (ML/analytics).
  // FIX-R35-HIGH-6 / FIX-R36-LOW-2: insertPensieveEvent now centralizes the
  // error-check + structured console.error. The local destructure is no
  // longer needed since the helper handles failure logging itself.
  await insertPensieveEvent({
    shop_id: params.shopId,
    ticket_id: params.ticketId,
    event_type: 'ticket.created',
    session_id: crypto.randomUUID(),
    timestamp: clock.now().toISOString(),
    metadata: {
      ticketType: params.ticketType,
      totalMinutes: params.totalMinutes,
      createdBy: params.userId,
      performed_by_user_id: params.userId,
    },
  });

  // OCIS dual-write (operational timeline)
  await logTicketEventToOcis({
    ticketId: params.ticketId,
    shopId: params.shopId,
    eventType: 'ticket_created',
    content: `Ticket created as ${params.ticketType} (${params.totalMinutes} min)`,
    authorUserId: params.userId,
    payload: {
      ticketType: params.ticketType,
      totalMinutes: params.totalMinutes,
    },
  });

  // Increment advisor metrics for ticket creation.
  // FIX-R132-MED-3: parallel to R36-LOW-2 (which centralized pensieve_events
  // error handling), the advisor_daily_metrics + waiter_promise_tracking
  // inserts here previously had no error destructure — silent telemetry
  // drops for the metrics that drive the WaiterPromiseWidget and the
  // advisor performance dashboard. Add structured logging.
  const { data: existing } = await supabase
    .from('advisor_daily_metrics')
    .select('id, tickets_created, waiter_count, dropoff_count')
    .eq('shop_id', params.shopId)
    .eq('user_id', params.userId)
    .eq('date', dateStr)
    .single();

  if (existing) {
    const { error: advisorUpdateErr } = await supabase
      .from('advisor_daily_metrics')
      .update({
        tickets_created: (existing.tickets_created || 0) + 1,
        waiter_count: params.ticketType === 'Waiter' ? (existing.waiter_count || 0) + 1 : existing.waiter_count,
        dropoff_count: params.ticketType === 'Drop-off' ? (existing.dropoff_count || 0) + 1 : existing.dropoff_count,
      })
      .eq('id', existing.id);
    if (advisorUpdateErr) {
      console.error('[ticketEventLogger] advisor_daily_metrics update failed:', advisorUpdateErr, {
        shop_id: params.shopId,
        user_id: params.userId,
        date: dateStr,
      });
    }
  } else {
    const { error: advisorInsertErr } = await supabase
      .from('advisor_daily_metrics')
      .insert({
        shop_id: params.shopId,
        user_id: params.userId,
        date: dateStr,
        tickets_created: 1,
        waiter_count: params.ticketType === 'Waiter' ? 1 : 0,
        dropoff_count: params.ticketType === 'Drop-off' ? 1 : 0,
      });
    if (advisorInsertErr) {
      console.error('[ticketEventLogger] advisor_daily_metrics insert failed:', advisorInsertErr, {
        shop_id: params.shopId,
        user_id: params.userId,
        date: dateStr,
      });
    }
  }

  // Track waiter promise if applicable
  if (params.ticketType === 'Waiter' && params.promisedTime && params.scheduledStartTime) {
    const { error: promiseInsertErr } = await supabase.from('waiter_promise_tracking').insert({
      shop_id: params.shopId,
      ticket_id: params.ticketId,
      promised_time: params.promisedTime,
      scheduled_start_time: params.scheduledStartTime,
    });
    if (promiseInsertErr) {
      console.error('[ticketEventLogger] waiter_promise_tracking insert failed:', promiseInsertErr, {
        shop_id: params.shopId,
        ticket_id: params.ticketId,
      });
    }
  }
}

/**
 * Log ticket edit event
 */
export async function logTicketEdited(params: {
  ticketId: string;
  shopId: string;
  userId: string;
  changes: Record<string, any>;
  shopTimezone?: string;
}) {
  // R8-L-3: Use shop TZ for daily metrics key
  const dateStr = params.shopTimezone ? getTodayInShopTz(params.shopTimezone) : getTodayInShopTz('UTC');

  // Log pensieve event. FIX-R35-HIGH-6 / FIX-R36-LOW-2: helper now handles
  // the error-check + structured console.error.
  await insertPensieveEvent({
    shop_id: params.shopId,
    ticket_id: params.ticketId,
    event_type: 'ticket.edited',
    session_id: crypto.randomUUID(),
    timestamp: clock.now().toISOString(),
    metadata: {
      changes: params.changes,
      editedBy: params.userId,
      performed_by_user_id: params.userId,
    },
  });

  // OCIS dual-write for ticket edited.
  // OCIS-R66-FIX: previously labelled every edit as 'ticket_date_changed' even
  // when the change was status, priority, notes, etc. — that corrupted the
  // timeline semantics (a note update showed up as a "Date Changed" event on
  // the UI). Route to the correct enum based on which fields actually
  // changed; skip OCIS when the edit has no semantic mapping (Pensieve still
  // captures it for analytics above). A dedicated 'ticket_edited' enum value
  // is flagged as a PRODUCT item.
  const changeKeys = Object.keys(params.changes);
  const dateFields = ['scheduled_date', 'start_time', 'opti_finish', 'promised_time'];
  const statusFields = ['status'];
  const dateChanged = changeKeys.some(k => dateFields.includes(k));
  const statusChanged = changeKeys.some(k => statusFields.includes(k));
  const changesSummary = changeKeys.join(', ');

  // OCIS-R68-FIX (HIGH-2): emit BOTH events when an edit changes both the
  // schedule and the status. Previously this used `if / else if` so a single
  // update that e.g. moved a ticket to a new day AND marked it Paused would
  // log only the date event — the payload carried the status change too but
  // the UI labelled the row "Date Changed", hiding the status transition on
  // the operational timeline. Status transitions are load-bearing for
  // downstream analytics (time-in-status, on-time completion), so we emit
  // them independently. The payload on each event intentionally carries the
  // full `changes` object so readers that want cross-reference still have
  // it, but the event_type is now semantically accurate for each row.
  if (dateChanged) {
    await logTicketEventToOcis({
      ticketId: params.ticketId,
      shopId: params.shopId,
      eventType: 'ticket_date_changed',
      content: `Date/schedule changed: ${changesSummary}`,
      authorUserId: params.userId,
      payload: params.changes,
    });
  }
  if (statusChanged) {
    await logTicketEventToOcis({
      ticketId: params.ticketId,
      shopId: params.shopId,
      eventType: 'ticket_status_changed',
      content: `Status changed${params.changes.status ? ` to ${params.changes.status}` : ''}`,
      authorUserId: params.userId,
      payload: params.changes,
    });
  }
  // Other edits (notes, priority, assignment metadata) are intentionally
  // NOT written to OCIS today — we do not have a generic 'ticket_edited'
  // enum value and using an unrelated event type corrupts the timeline.

  // Increment advisor metrics for ticket edit
  const { data: existing } = await supabase
    .from('advisor_daily_metrics')
    .select('id, tickets_edited')
    .eq('shop_id', params.shopId)
    .eq('user_id', params.userId)
    .eq('date', dateStr)
    .single();

  if (existing) {
    await supabase
      .from('advisor_daily_metrics')
      .update({ tickets_edited: (existing.tickets_edited || 0) + 1 })
      .eq('id', existing.id);
  } else {
    await supabase
      .from('advisor_daily_metrics')
      .insert({
        shop_id: params.shopId,
        user_id: params.userId,
        date: dateStr,
        tickets_edited: 1,
      });
  }
}

/**
 * Log waiter conversion event
 */
export async function logWaiterConversion(params: {
  ticketId: string;
  shopId: string;
  userId: string;
  reason: string;
  shopTimezone?: string;
}) {
  // R8-L-3: Use shop TZ for daily metrics key
  const dateStr = params.shopTimezone ? getTodayInShopTz(params.shopTimezone) : getTodayInShopTz('UTC');

  // Log pensieve event
  await insertPensieveEvent({
    shop_id: params.shopId,
    ticket_id: params.ticketId,
    event_type: 'waiter.converted_to_dropoff',
    session_id: crypto.randomUUID(),
    timestamp: clock.now().toISOString(),
    metadata: {
      reason: params.reason,
      convertedBy: params.userId,
      performed_by_user_id: params.userId,
    },
  });

  // Increment advisor metrics for waiter conversion
  const { data: existing } = await supabase
    .from('advisor_daily_metrics')
    .select('id, waiter_conversions')
    .eq('shop_id', params.shopId)
    .eq('user_id', params.userId)
    .eq('date', dateStr)
    .single();

  if (existing) {
    await supabase
      .from('advisor_daily_metrics')
      .update({ waiter_conversions: (existing.waiter_conversions || 0) + 1 })
      .eq('id', existing.id);
  } else {
    await supabase
      .from('advisor_daily_metrics')
      .insert({
        shop_id: params.shopId,
        user_id: params.userId,
        date: dateStr,
        waiter_conversions: 1,
      });
  }
}

/**
 * Log job time edited event (dual-write: Pensieve + OCIS)
 */
export async function logJobTimeEdited(params: {
  ticketId: string;
  shopId: string;
  userId: string;
  jobName: string;
  previousMinutes: number;
  newMinutes: number;
}) {
  const prevHours = (params.previousMinutes / 60).toFixed(1);
  const newHours = (params.newMinutes / 60).toFixed(1);

  // Pensieve
  await insertPensieveEvent({
    shop_id: params.shopId,
    ticket_id: params.ticketId,
    event_type: 'job.time_edited',
    session_id: crypto.randomUUID(),
    timestamp: clock.now().toISOString(),
    metadata: {
      job_name: params.jobName,
      previous_minutes: params.previousMinutes,
      new_minutes: params.newMinutes,
      performed_by_user_id: params.userId,
    },
  });

  // OCIS dual-write
  await logTicketEventToOcis({
    ticketId: params.ticketId,
    shopId: params.shopId,
    eventType: 'job_time_edited',
    content: `${params.jobName}: ${prevHours}h → ${newHours}h`,
    authorUserId: params.userId,
    payload: {
      jobName: params.jobName,
      previousMinutes: params.previousMinutes,
      newMinutes: params.newMinutes,
    },
  });
}

/**
 * Log policy/slider change event (Core Tier)
 */
export async function logPolicyChange(params: {
  shopId: string;
  userId: string;
  sliderName: string;
  oldValue: any;
  newValue: any;
}) {
  await insertPensieveEvent({
    shop_id: params.shopId,
    ticket_id: 'shop_settings', // Placeholder since this isn't ticket-specific
    event_type: 'policy.change',
    session_id: crypto.randomUUID(),
    timestamp: clock.now().toISOString(),
    metadata: {
      slider_name: params.sliderName,
      old_value: params.oldValue,
      new_value: params.newValue,
      user_id: params.userId,
      performed_by_user_id: params.userId,
    },
  });
}

/**
 * Log advisor override event (drag-drop / forced assignment) (Core Tier)
 */
export async function logAdvisorOverride(params: {
  ticketId: string;
  shopId: string;
  userId: string;
  fromTechId: string | null;
  toTechId: string;
  shopTimezone?: string;
}) {
  // R8-L-3: Use shop TZ for daily metrics key
  const dateStr = params.shopTimezone ? getTodayInShopTz(params.shopTimezone) : getTodayInShopTz('UTC');

  // Log pensieve event
  await insertPensieveEvent({
    shop_id: params.shopId,
    ticket_id: params.ticketId,
    event_type: 'override.advisor',
    session_id: crypto.randomUUID(),
    timestamp: clock.now().toISOString(),
    metadata: {
      from_tech: params.fromTechId,
      to_tech: params.toTechId,
      user_id: params.userId,
      performed_by_user_id: params.userId,
    },
  });

  // OCIS-R68-FIX (CRIT-1): Do NOT emit `ticket_reassigned` here. Every
  // production caller of logAdvisorOverride (currently Dashboard.tsx drag-
  // drop) routes the reassignment through the `reassign-ticket` edge
  // function before calling this helper, and `reassignCore.executeReassignment`
  // already emits `ticket_reassigned` via `insertOcisThreadEvent` (see
  // reassignCore.ts:1457). Emitting again client-side produced **two**
  // identical `ticket_reassigned` events on the timeline for every
  // drag-drop, double-counting reassigns in any analytics and
  // corrupting the single-source-of-truth guarantee OCIS is supposed
  // to provide. The Pensieve `override.advisor` event + the
  // scheduling_overrides metric below remain here because those are
  // client-side advisor-attribution concerns and reassignCore does not
  // emit them. If a future caller reassigns WITHOUT going through
  // reassign-ticket, add the OCIS emit back in that caller's path (not
  // here) — server-side is the authoritative OCIS write for reassigns.

  // Increment scheduling_overrides in advisor_daily_metrics
  const { data: existing } = await supabase
    .from('advisor_daily_metrics')
    .select('id, scheduling_overrides')
    .eq('shop_id', params.shopId)
    .eq('user_id', params.userId)
    .eq('date', dateStr)
    .single();

  if (existing) {
    await supabase
      .from('advisor_daily_metrics')
      .update({ scheduling_overrides: (existing.scheduling_overrides || 0) + 1 })
      .eq('id', existing.id);
  } else {
    await supabase
      .from('advisor_daily_metrics')
      .insert({
        shop_id: params.shopId,
        user_id: params.userId,
        date: dateStr,
        scheduling_overrides: 1,
      });
  }
}

/**
 * Log timer paused event (dual-write: Pensieve + OCIS)
 */
export async function logTimerPaused(params: {
  ticketId: string;
  shopId: string;
  userId: string;
  pauseReason: string;
}) {
  // Pensieve (ML/analytics)
  await insertPensieveEvent({
    shop_id: params.shopId,
    ticket_id: params.ticketId,
    event_type: 'timer.paused',
    session_id: crypto.randomUUID(),
    timestamp: clock.now().toISOString(),
    metadata: {
      pause_reason: params.pauseReason,
      paused_by: params.userId,
      performed_by_user_id: params.userId,
    },
  });

  // OCIS dual-write
  await logTicketEventToOcis({
    ticketId: params.ticketId,
    shopId: params.shopId,
    eventType: 'ticket_paused',
    content: `Timer paused: ${params.pauseReason}`,
    authorUserId: params.userId,
    payload: { pauseReason: params.pauseReason },
  });
}

/**
 * Log timer resumed event (dual-write: Pensieve + OCIS)
 */
export async function logTimerResumed(params: {
  ticketId: string;
  shopId: string;
  userId: string;
}) {
  // Pensieve
  await insertPensieveEvent({
    shop_id: params.shopId,
    ticket_id: params.ticketId,
    event_type: 'timer.resumed',
    session_id: crypto.randomUUID(),
    timestamp: clock.now().toISOString(),
    metadata: {
      resumed_by: params.userId,
      performed_by_user_id: params.userId,
    },
  });

  // OCIS dual-write
  await logTicketEventToOcis({
    ticketId: params.ticketId,
    shopId: params.shopId,
    eventType: 'ticket_resumed',
    content: 'Timer resumed',
    authorUserId: params.userId,
  });
}

/**
 * Log ticket moved to parking lot event
 */
export async function logMovedToParkingLot(params: {
  ticketId: string;
  shopId: string;
  userId: string;
  fromTechId?: string | null;
  reason?: string;
}) {
  console.log('[logMovedToParkingLot] Called with params:', params);
  
  if (!params.shopId || !params.ticketId) {
    console.error('[logMovedToParkingLot] Missing required params:', params);
    throw new Error('Missing required params for logMovedToParkingLot');
  }
  
  if (!params.userId) {
    console.error('[logMovedToParkingLot] Missing userId:', params);
    throw new Error('Missing userId for logMovedToParkingLot');
  }

  // Resolve technician name if fromTechId provided
  let fromTechName: string | null = null;
  if (params.fromTechId) {
    try {
      const { data: tech } = await supabase
        .from('technicians')
        .select('name')
        .eq('id', params.fromTechId)
        .maybeSingle();
      fromTechName = tech?.name ?? null;
    } catch (e) {
      console.warn('[logMovedToParkingLot] Could not resolve tech name:', e);
    }
  }

  const contentParts: string[] = [];
  if (fromTechName) contentParts.push(fromTechName);
  contentParts.push('→ Parking Lot');
  if (params.reason) contentParts.push(`(${params.reason})`);
  const content = contentParts.join(' ');
  
  const insertData = {
    shop_id: params.shopId,
    ticket_id: params.ticketId,
    event_type: 'ticket.moved_to_parking_lot',
    session_id: crypto.randomUUID(),
    timestamp: clock.now().toISOString(),
    metadata: {
      moved_by: params.userId,
      performed_by_user_id: params.userId,
      from_tech_id: params.fromTechId || null,
      from_tech_name: fromTechName,
      reason: params.reason || 'Manual move',
    },
  };
  
  console.log('[logMovedToParkingLot] Inserting event:', insertData);
  
  // Run pensieve and OCIS writes independently so one failure doesn't block the other
  const pensievePromise = supabase.from('pensieve_events').insert(insertData).select()
    .then(({ data, error }) => {
      if (error) console.error('[logMovedToParkingLot] Pensieve insert failed:', error);
      else console.log('[logMovedToParkingLot] Pensieve insert success:', data);
    });

  const ocisPromise = logTicketEventToOcis({
    ticketId: params.ticketId,
    shopId: params.shopId,
    eventType: 'ticket_moved_to_parking_lot',
    content,
    authorUserId: params.userId,
    payload: {
      fromTechId: params.fromTechId,
      fromTechName,
      reason: params.reason,
    },
  }).catch(err => console.error('[logMovedToParkingLot] OCIS write failed:', err));

  await Promise.all([pensievePromise, ocisPromise]);
}

/**
 * Log ticket moved from parking lot event
 */
export async function logMovedFromParkingLot(params: {
  ticketId: string;
  shopId: string;
  userId: string;
  toTechId: string;
  toTechName?: string;
}) {
  // Resolve tech name if not provided
  let toTechName = params.toTechName || null;
  if (!toTechName && params.toTechId) {
    try {
      const { data: tech } = await supabase
        .from('technicians')
        .select('name')
        .eq('id', params.toTechId)
        .maybeSingle();
      toTechName = tech?.name ?? null;
    } catch (e) {
      console.warn('[logMovedFromParkingLot] Could not resolve tech name:', e);
    }
  }

  const content = `Parking Lot → ${toTechName || 'technician'}`;

  await insertPensieveEvent({
    shop_id: params.shopId,
    ticket_id: params.ticketId,
    event_type: 'ticket.moved_from_parking_lot',
    session_id: crypto.randomUUID(),
    timestamp: clock.now().toISOString(),
    metadata: {
      moved_by: params.userId,
      performed_by_user_id: params.userId,
      to_tech_id: params.toTechId,
      to_tech_name: toTechName,
    },
  });

  // OCIS dual-write
  await logTicketEventToOcis({
    ticketId: params.ticketId,
    shopId: params.shopId,
    eventType: 'ticket_moved_from_parking_lot',
    content,
    authorUserId: params.userId,
    payload: {
      toTechId: params.toTechId,
      toTechName,
    },
  });
}

/**
 * Log ticket date changed event
 */
export async function logDateChanged(params: {
  ticketId: string;
  shopId: string;
  userId: string;
  oldDate: string;
  newDate: string;
}) {
  // Fetch shop_id if not provided
  let shopId = params.shopId;
  if (!shopId) {
    const { data: ticket } = await supabase
      .from('tickets')
      .select('shop_id')
      .eq('id', params.ticketId)
      .single();
    shopId = ticket?.shop_id || '';
  }

  await insertPensieveEvent({
    shop_id: shopId,
    ticket_id: params.ticketId,
    event_type: 'ticket.date_changed',
    session_id: crypto.randomUUID(),
    timestamp: clock.now().toISOString(),
    metadata: {
      changed_by: params.userId,
      performed_by_user_id: params.userId,
      old_date: params.oldDate,
      new_date: params.newDate,
    },
  });

  // OCIS dual-write
  await logTicketEventToOcis({
    ticketId: params.ticketId,
    shopId: shopId,
    eventType: 'ticket_date_changed',
    content: `Date changed from ${params.oldDate} to ${params.newDate}`,
    authorUserId: params.userId,
    payload: {
      oldDate: params.oldDate,
      newDate: params.newDate,
    },
  });
}

/**
 * Log ticket completed event (dual-write: Pensieve + OCIS)
 */
export async function logTicketCompleted(params: {
  ticketId: string;
  shopId: string;
  userId: string;
  completedByTechId?: string | null;
  actualMinutes?: number;
  timerWasRunning?: boolean;
}) {
  // Pensieve
  await insertPensieveEvent({
    shop_id: params.shopId,
    ticket_id: params.ticketId,
    event_type: 'ticket.completed',
    session_id: crypto.randomUUID(),
    timestamp: clock.now().toISOString(),
    metadata: {
      completed_by_user: params.userId,
      performed_by_user_id: params.userId,
      completed_by_tech: params.completedByTechId || null,
      actual_minutes: params.actualMinutes || null,
    },
  });

  // Guardrail 1: emit timer_stopped FIRST if timer was running
  if (params.timerWasRunning) {
    await logTicketEventToOcis({
      ticketId: params.ticketId,
      shopId: params.shopId,
      eventType: 'timer_stopped',
      content: 'Timer stopped (ticket completed)',
      authorUserId: params.userId,
      payload: { stoppedBy: params.userId },
    });
  }

  // OCIS dual-write for ticket_completed
  await logTicketEventToOcis({
    ticketId: params.ticketId,
    shopId: params.shopId,
    eventType: 'ticket_completed',
    content: `Ticket completed${params.actualMinutes ? ` (${params.actualMinutes} min actual)` : ''}`,
    authorUserId: params.userId,
    payload: {
      completedByTechId: params.completedByTechId,
      actualMinutes: params.actualMinutes,
    },
  });
}

/**
 * Log bay changed event (dual-write: Pensieve + OCIS)
 */
export async function logBayChanged(params: {
  ticketId: string;
  shopId: string;
  userId: string;
  fromBay: string | null;
  toBay: string;
}) {
  await logTicketEventToOcis({
    ticketId: params.ticketId,
    shopId: params.shopId,
    eventType: 'ticket_bay_changed',
    content: `Bay changed${params.fromBay ? ` from ${params.fromBay}` : ''} to ${params.toBay}`,
    authorUserId: params.userId,
    payload: { fromBay: params.fromBay, toBay: params.toBay, changedBy: params.userId },
  });
}

/**
 * Log status changed event (dual-write: Pensieve + OCIS)
 */
export async function logStatusChanged(params: {
  ticketId: string;
  shopId: string;
  userId: string;
  fromStatus: string;
  toStatus: string;
}) {
  await logTicketEventToOcis({
    ticketId: params.ticketId,
    shopId: params.shopId,
    eventType: 'ticket_status_changed',
    content: `Status changed from ${params.fromStatus} to ${params.toStatus}`,
    authorUserId: params.userId,
    payload: { fromStatus: params.fromStatus, toStatus: params.toStatus, changedBy: params.userId },
  });
}

/**
 * Log ticket assigned event
 */
export async function logTicketAssigned(params: {
  ticketId: string;
  shopId: string;
  userId: string;
  techId: string;
  techName?: string;
  startTime?: string;
}) {
  await insertPensieveEvent({
    shop_id: params.shopId,
    ticket_id: params.ticketId,
    event_type: 'ticket.assigned',
    session_id: crypto.randomUUID(),
    timestamp: clock.now().toISOString(),
    metadata: {
      assigned_by: params.userId,
      performed_by_user_id: params.userId,
      tech_id: params.techId,
      tech_name: params.techName || null,
      start_time: params.startTime || null,
    },
  });

  // OCIS dual-write
  await logTicketEventToOcis({
    ticketId: params.ticketId,
    shopId: params.shopId,
    eventType: 'ticket_assigned',
    content: `Ticket assigned to ${params.techName || 'technician'}`,
    authorUserId: params.userId,
    payload: {
      techId: params.techId,
      techName: params.techName,
      startTime: params.startTime,
    },
  });
}

/**
 * Log auto-bump event (consolidated - updates existing or creates new)
 */
export async function logAutoBump(params: {
  ticketId: string;
  shopId: string;
  deltaMinutes: number;
  shopTimezone?: string;
}) {
  // R8-L-3: Use shop TZ for daily metrics key
  const dateStr = params.shopTimezone ? getTodayInShopTz(params.shopTimezone) : getTodayInShopTz('UTC');
  
  // R12-L-6: Convert shop-local day bounds to UTC for timestamptz dedup
  const dedupStartUtc = params.shopTimezone
    ? fromZonedTime(`${dateStr}T00:00:00`, params.shopTimezone).toISOString()
    : `${dateStr}T00:00:00`;
  const dedupEndUtc = params.shopTimezone
    ? fromZonedTime(`${dateStr}T23:59:59`, params.shopTimezone).toISOString()
    : `${dateStr}T23:59:59`;

  // Check if there's already an auto-bump event for this ticket today
  const { data: existing } = await supabase
    .from('pensieve_events')
    .select('id, metadata')
    .eq('ticket_id', params.ticketId)
    .eq('event_type', 'ticket.auto_bumped')
    .gte('timestamp', dedupStartUtc)
    .lte('timestamp', dedupEndUtc)
    .maybeSingle();

  if (existing) {
    // Update existing event - add to total
    const currentTotal = (existing.metadata as any)?.total_minutes || 0;
    await supabase
      .from('pensieve_events')
      .update({
        metadata: {
          total_minutes: currentTotal + params.deltaMinutes,
          last_bump_at: clock.now().toISOString(),
          bump_count: ((existing.metadata as any)?.bump_count || 1) + 1,
        },
      })
      .eq('id', existing.id);
  } else {
    // Create new event
    await insertPensieveEvent({
      shop_id: params.shopId,
      ticket_id: params.ticketId,
      event_type: 'ticket.auto_bumped',
      session_id: crypto.randomUUID(),
      timestamp: clock.now().toISOString(),
      metadata: {
        total_minutes: params.deltaMinutes,
        bump_count: 1,
        last_bump_at: clock.now().toISOString(),
      },
    });
  }

  // OCIS-R66-FIX (HIGH-5): the 'auto_bump' enum has been in the DB since v1
  // and ThreadEventItem has an EVENT_CONFIG entry for it, but nothing wrote
  // it until now — so the operational timeline silently dropped every auto-
  // bump. Emit to OCIS on every call (not only first-of-day) so each bump is
  // visible in the timeline; payload carries the delta for analytics.
  await logTicketEventToOcis({
    ticketId: params.ticketId,
    shopId: params.shopId,
    eventType: 'auto_bump',
    content: `Auto-bumped by ${params.deltaMinutes} min`,
    payload: {
      deltaMinutes: params.deltaMinutes,
    },
  });
}

/**
 * Log job added event (dual-write: Pensieve + OCIS)
 */
export async function logJobAdded(params: {
  ticketId: string;
  shopId: string;
  userId: string;
  jobId: string;
  jobName: string;
  minutes: number;
}) {
  // Pensieve (ML/analytics)
  await insertPensieveEvent({
    shop_id: params.shopId,
    ticket_id: params.ticketId,
    event_type: 'job.added',
    job_id: params.jobId,
    session_id: crypto.randomUUID(),
    timestamp: clock.now().toISOString(),
    metadata: {
      added_by: params.userId,
      performed_by_user_id: params.userId,
      job_name: params.jobName,
      minutes: params.minutes,
    },
  });

  // OCIS dual-write (operational timeline)
  await logTicketEventToOcis({
    ticketId: params.ticketId,
    shopId: params.shopId,
    eventType: 'job_added',
    content: `Added job: ${params.jobName} (${params.minutes} min)`,
    authorUserId: params.userId,
    payload: {
      jobId: params.jobId,
      jobName: params.jobName,
      minutes: params.minutes,
    },
  });
}

/**
 * Log job deleted event (dual-write: Pensieve + OCIS)
 */
export async function logJobDeleted(params: {
  ticketId: string;
  shopId: string;
  userId: string;
  jobId: string;
  jobName: string;
}) {
  // Pensieve (ML/analytics)
  await insertPensieveEvent({
    shop_id: params.shopId,
    ticket_id: params.ticketId,
    event_type: 'job.deleted',
    job_id: params.jobId,
    session_id: crypto.randomUUID(),
    timestamp: clock.now().toISOString(),
    metadata: {
      deleted_by: params.userId,
      performed_by_user_id: params.userId,
      job_name: params.jobName,
    },
  });

  // OCIS dual-write (operational timeline)
  await logTicketEventToOcis({
    ticketId: params.ticketId,
    shopId: params.shopId,
    eventType: 'job_removed',
    content: `Removed job: ${params.jobName}`,
    authorUserId: params.userId,
    payload: {
      jobId: params.jobId,
      jobName: params.jobName,
    },
  });
}

/**
 * Log job note added/updated to OCIS (for Thread tab visibility)
 */
export async function logJobNoteToOcis(params: {
  ticketId: string;
  shopId: string;
  userId: string;
  jobId: string;
  jobName: string;
  noteContent: string;
  isUpdate: boolean; // true = note was updated, false = note was added
}) {
  await logTicketEventToOcis({
    ticketId: params.ticketId,
    shopId: params.shopId,
    eventType: params.isUpdate ? 'job_note_updated' : 'job_note_added',
    content: `${params.isUpdate ? 'Updated' : 'Added'} note on ${params.jobName}: "${params.noteContent}"`,
    authorUserId: params.userId,
    payload: {
      jobId: params.jobId,
      jobName: params.jobName,
      noteContent: params.noteContent,
    },
  });
}

/**
 * Log timer started event (dual-write: Pensieve + OCIS)
 */
export async function logTimerStarted(params: {
  ticketId: string;
  shopId: string;
  userId: string;
  techId: string;
  earlyStart?: boolean;
}) {
  await insertPensieveEvent({
    shop_id: params.shopId,
    ticket_id: params.ticketId,
    event_type: 'timer.started',
    tech_id: params.techId,
    session_id: crypto.randomUUID(),
    timestamp: clock.now().toISOString(),
    metadata: {
      started_by: params.userId,
      performed_by_user_id: params.userId,
      early_start: params.earlyStart || false,
    },
  });

  // OCIS dual-write
  await logTicketEventToOcis({
    ticketId: params.ticketId,
    shopId: params.shopId,
    eventType: 'ticket_started',
    content: `Timer started${params.earlyStart ? ' (early start)' : ''}`,
    authorUserId: params.userId,
    payload: { techId: params.techId, earlyStart: params.earlyStart || false },
  });
}

/**
 * Log vehicle present status changed event
 */
export async function logVehiclePresentChanged(params: {
  ticketId: string;
  shopId: string;
  userId: string;
  vehiclePresent: boolean;
}) {
  await insertPensieveEvent({
    shop_id: params.shopId,
    ticket_id: params.ticketId,
    event_type: 'ticket.vehicle_present_changed',
    session_id: crypto.randomUUID(),
    timestamp: clock.now().toISOString(),
    metadata: {
      vehicle_present: params.vehiclePresent,
      performed_by_user_id: params.userId,
    },
  });
}

/**
 * Log ticket split event
 */
export async function logTicketSplit(params: {
  parentTicketId: string;
  childTicketIds: string[];
  shopId: string;
  userId: string;
  originalTechId: string | null;
  splitReason: 'manual_split' | 'specialist_gap_split';
  vehicleInfo?: { year?: number; make?: string; model?: string };
  jobAssignments: Array<{
    ticketId: string;
    techId: string;
    techName: string;
    jobCategories: string[];
  }>;
}) {
  const sessionId = crypto.randomUUID();
  const timestamp = clock.now().toISOString();

  // Main split event
  await insertPensieveEvent({
    shop_id: params.shopId,
    ticket_id: params.parentTicketId,
    event_type: 'ticket.split',
    session_id: sessionId,
    timestamp,
    metadata: {
      split_type: params.splitReason,
      performed_by_user_id: params.userId,
      parent_ticket_id: params.parentTicketId,
      child_ticket_ids: params.childTicketIds,
      original_tech_id: params.originalTechId,
      new_tech_assignments: params.jobAssignments,
      vehicle_year: params.vehicleInfo?.year,
      vehicle_make: params.vehicleInfo?.make,
      vehicle_model: params.vehicleInfo?.model,
    },
  });

  // Log preference signals for job reassignments
  for (const assignment of params.jobAssignments) {
    if (assignment.techId !== params.originalTechId) {
      // Positive signal for new tech
      await insertPensieveEvent({
        shop_id: params.shopId,
        ticket_id: assignment.ticketId,
        event_type: 'preference.positive',
        session_id: sessionId,
        timestamp,
        tech_id: assignment.techId,
        metadata: {
          signal_source: params.splitReason,
          performed_by_user_id: params.userId,
          job_categories: assignment.jobCategories,
          vehicle_make: params.vehicleInfo?.make,
          vehicle_year_group: params.vehicleInfo?.year 
            ? Math.floor(params.vehicleInfo.year / 5) * 5 
            : null,
        },
      });

      // Negative signal for original tech
      if (params.originalTechId) {
        await insertPensieveEvent({
          shop_id: params.shopId,
          ticket_id: params.parentTicketId,
          event_type: 'preference.negative',
          session_id: sessionId,
          timestamp,
          tech_id: params.originalTechId,
          metadata: {
            signal_source: params.splitReason,
            performed_by_user_id: params.userId,
            job_categories: assignment.jobCategories,
            vehicle_make: params.vehicleInfo?.make,
            vehicle_year_group: params.vehicleInfo?.year 
              ? Math.floor(params.vehicleInfo.year / 5) * 5 
              : null,
          },
        });
      }
    }
  }
}

/**
 * Log service information acknowledgment event (OCIS only — culture signal, not analytics)
 */
export async function logServiceInfoAcknowledgment(params: {
  ticketId: string;
  shopId: string;
  userId: string;
  techId: string;
  acknowledged: boolean;
  sources?: string[];
  note?: string;
  sessionContext: 'start' | 'resume';
}) {
  const content = params.acknowledged
    ? `Service information acknowledged${params.sources?.length ? ` (${params.sources.join(', ')})` : ''}`
    : 'Service information not yet consulted';

  await logTicketEventToOcis({
    ticketId: params.ticketId,
    shopId: params.shopId,
    eventType: 'service_info_acknowledgment',
    content,
    authorUserId: params.userId,
    authorRole: 'technician',
    payload: {
      acknowledged: params.acknowledged,
      sources: params.sources || [],
      note: params.note || null,
      session_context: params.sessionContext,
      tech_id: params.techId,
    },
  });
}

/**
 * Log manager edit of a service information acknowledgment (creates new event, preserves original)
 */
export async function logServiceInfoAcknowledgmentUpdate(params: {
  ticketId: string;
  shopId: string;
  userId: string;
  originalEventId: string;
  acknowledged: boolean;
  sources?: string[];
  note?: string;
}) {
  await logTicketEventToOcis({
    ticketId: params.ticketId,
    shopId: params.shopId,
    eventType: 'service_info_acknowledgment_updated',
    content: 'Service information acknowledgment updated by manager',
    authorUserId: params.userId,
    authorRole: 'manager',
    payload: {
      original_event_id: params.originalEventId,
      acknowledged: params.acknowledged,
      sources: params.sources || [],
      note: params.note || null,
    },
  });
}
